import { MedicalTreatmentContactPreview } from "./MedicalTreatmentContactPreview";
import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiselectFormField } from "@/components/ui/FormFields/MultiselectFormField/MultiselectFormField";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useSyncMedicalTreatmentContactConnections } from "@/lib/data/react-query/connections/medical-treatment-contact-connection";
import {
  useCreateMedicalTreatment,
  useDeleteMedicalTreatment,
  useUpdateMedicalTreatment,
} from "@/lib/data/react-query/matters/medical-treatment";
import { useUpdateMedicalTreatmentBill } from "@/lib/data/react-query/matters/medical-treatment-bill";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas";
import { MedicalTreatmentSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import {
  MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  Contact,
  MedicalTreatment,
  MedicalTreatmentBill,
} from "@/lib/types";
import { formatLocalOrIntlPhoneNumber as formatPhone } from "@/lib/utils/format";
import {
  getConnectionFormContact,
  getConnectionPreviewContact,
} from "@/lib/utils/connections/item-contact-connections";
import {
  createMedicalTreatmentContactConnectionChildInput,
  getMedicalTreatmentContactConnection,
  type SupportedMedicalTreatmentContactRole,
} from "@/lib/utils/connections/medical-treatment-contact-connections";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { isNil, omit, omitBy } from "lodash";
import { Mail, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditMedicalTreatmentSlideoverProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatment>;
  initialProvider?: Contact;
}

const treatmentFormSchema = MedicalTreatmentSchema.extend({
  matterContactConnectionMedicalProvider: ContactConnectionInFormSchema,
  matterContactConnectionTreatingPhysician: ContactConnectionInFormSchema,
  matterContactConnectionLienHolder: ContactConnectionInFormSchema,
  matterContactConnectionLienRepresentative: ContactConnectionInFormSchema,
  type: z
    .array(z.enum(CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS))
    .optional(),
})
  .omit({
    connections: true,
    connectionsNextCursor: true,
    dateCreated: true,
    dateModified: true,
    matterId: true,
    id: true,
  })
  .superRefine((data, ctx) => {
    if (data.type?.length) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one treatment type is required",
      path: ["type"],
    });
  });

const defaultFormValues = {
  description: "",
  nextSteps: "",
  recommendations: "",
  procedures: "",
  status: "",
  portalLink: "",
  portalDetails: "",
  patientAccountNumber: "",
  letterOfProtection: undefined,
  lien: undefined,
  billNotApplicable: false,
  planNotApplicable: false,
  recordNotApplicable: false,
  soap: "",
  type: [],
  matterContactConnectionMedicalProvider:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionTreatingPhysician:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionLienHolder: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionLienRepresentative:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
};

const MEDICAL_TREATMENT_FORM_ID = "lw-medical-treatment-form";

export const AddOrEditMedicalTreatmentSlideover = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  treatmentId,
  initialData,
  initialProvider,
}: AddOrEditMedicalTreatmentSlideoverProps) => {
  const queryClient = useQueryClient();
  const { hasActionPermission } = useUser();
  const { updateSlideoverPayload } = useModals();

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  const selectedTreatment = treatmentId
    ? matter.medicalTreatments.find((treatment) => treatment.id === treatmentId)
    : undefined;
  const treatmentInitialValues = mergeInitialData(
    selectedTreatment,
    initialData,
  );

  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const completeTreatment = completeMedicalTreatments.find(
    (t) => t.id === selectedTreatment?.id,
  );

  const provider = getMedicalTreatmentContactConnection(
    selectedTreatment,
    "Provider",
  );
  const treatingPhysician = getMedicalTreatmentContactConnection(
    selectedTreatment,
    "Treating Physician",
  );
  const lienHolder = getMedicalTreatmentContactConnection(
    selectedTreatment,
    "Lien Holder",
  );
  const lienRepresentative = getMedicalTreatmentContactConnection(
    selectedTreatment,
    "Lien Representative",
  );

  const treatmentForm = useForm<z.infer<typeof treatmentFormSchema>>({
    resolver: zodResolver(treatmentFormSchema),
    values:
      editing && treatmentInitialValues
        ? {
            ...defaultFormValues,
            ...treatmentInitialValues,
            matterContactConnectionMedicalProvider: provider
              ? (getConnectionFormContact(provider) ??
                (initialProvider?.id === provider.targetObjectId
                  ? initialProvider
                  : undefined) ?? {
                  contactName: "Unknown contact",
                  id: provider.targetObjectId,
                  type: "organization",
                })
              : defaultFormValues.matterContactConnectionMedicalProvider,
            matterContactConnectionTreatingPhysician:
              getConnectionFormContact(treatingPhysician),
            matterContactConnectionLienHolder:
              getConnectionFormContact(lienHolder),
            matterContactConnectionLienRepresentative:
              getConnectionFormContact(lienRepresentative),
          }
        : {
            ...defaultFormValues,
            matterContactConnectionMedicalProvider:
              initialProvider ??
              defaultFormValues.matterContactConnectionMedicalProvider,
          },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [selectedProvider, setSelectedProvider] = useState<Contact | null>(
    initialProvider ?? null,
  );
  const [, setSelectedTreatingPhysician] = useState<Contact | null>(null);
  const [, setSelectedLienHolder] = useState<Contact | null>(null);
  const [, setSelectedLienRepresentative] = useState<Contact | null>(null);

  const syncTreatmentConnections = useSyncMedicalTreatmentContactConnections();
  const createMedicalTreatment = useCreateMedicalTreatment({
    showSuccessToast: false,
  });
  const updateMedicalTreatment = useUpdateMedicalTreatment();
  const deleteMedicalTreatment = useDeleteMedicalTreatment({
    successMessage: "Medical treatment deleted successfully",
  });
  const updateTreatmentBill = useUpdateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });

  const isFormLoading =
    createMedicalTreatment.isPending ||
    updateMedicalTreatment.isPending ||
    syncTreatmentConnections.isPending;

  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const [isDeleteTreatmentOpen, setIsDeleteTreatmentOpen] = useState(false);
  const [isUpdateBillsModalOpen, setIsUpdateBillsModalOpen] = useState(false);
  const [pendingCloseAfterSave, setPendingCloseAfterSave] = useState(false);
  const [pendingBillUpdates, setPendingBillUpdates] = useState<
    {
      billId: string;
      dateModified: string;
      updates: Pick<MedicalTreatmentBill, "letterOfProtection" | "lien">;
    }[]
  >([]);
  const [pendingBillUpdateSummary, setPendingBillUpdateSummary] = useState<{
    total: number;
    lopCount: number;
    lienCount: number;
  } | null>(null);

  const buildPendingBillUpdates = (
    nextData: z.infer<typeof treatmentFormSchema>,
    currentTreatment: MedicalTreatment,
  ) => {
    const lopChanged =
      nextData.letterOfProtection !== currentTreatment.letterOfProtection;
    const lienChanged = nextData.lien !== currentTreatment.lien;
    if (!lopChanged && !lienChanged) {
      return null;
    }
    const nextLop = nextData.letterOfProtection;
    const nextLien = nextData.lien;
    const treatmentBills = matter.medicalTreatmentBills.filter(
      (bill) => bill.medicalTreatmentId === currentTreatment.id,
    );

    const summary = { total: 0, lopCount: 0, lienCount: 0 };
    const updates = treatmentBills.reduce<
      {
        billId: string;
        dateModified: string;
        updates: Pick<MedicalTreatmentBill, "letterOfProtection" | "lien">;
      }[]
    >((acc, bill) => {
      const updateFields: Pick<
        MedicalTreatmentBill,
        "letterOfProtection" | "lien"
      > = {};
      if (lopChanged && nextLop && !bill.letterOfProtection) {
        updateFields.letterOfProtection = nextLop;
        summary.lopCount += 1;
      }
      if (lienChanged && nextLien && !bill.lien) {
        updateFields.lien = nextLien;
        summary.lienCount += 1;
      }
      if (Object.keys(updateFields).length > 0) {
        acc.push({
          billId: bill.id,
          dateModified: bill.dateModified,
          updates: updateFields,
        });
      }
      return acc;
    }, []);

    summary.total = updates.length;
    if (summary.total === 0) {
      return null;
    }
    return { updates, summary };
  };

  const onSubmit = async (data: z.infer<typeof treatmentFormSchema>) => {
    const treatmentData = omit(data, [
      "matterContactConnectionMedicalProvider",
      "matterContactConnectionTreatingPhysician",
      "matterContactConnectionLienHolder",
      "matterContactConnectionLienRepresentative",
    ]);
    let resData;
    const contactIdsByRole = {
      Provider: data.matterContactConnectionMedicalProvider?.id,
      "Treating Physician": data.matterContactConnectionTreatingPhysician?.id,
      "Lien Holder": data.matterContactConnectionLienHolder?.id,
      "Lien Representative": data.matterContactConnectionLienRepresentative?.id,
    } satisfies Record<
      SupportedMedicalTreatmentContactRole,
      string | undefined
    >;
    let shouldCloseAfterSave = closeAfterSave;
    if (editing && selectedTreatment) {
      const result = await updateMedicalTreatment.mutateAsync({
        ...treatmentData,
        matterId: matter.id,
        dateModified: selectedTreatment.dateModified,
        id: selectedTreatment.id,
      });
      resData = result;

      await syncTreatmentConnections.mutateAsync({
        currentTreatment: selectedTreatment,
        desiredContactIdsByRole: contactIdsByRole,
        matterId: matter.id,
        treatmentId: result.data.id,
      });
    } else {
      const connections = Object.entries(contactIdsByRole).flatMap(
        ([role, contactId]) =>
          contactId
            ? [
                createMedicalTreatmentContactConnectionChildInput(
                  contactId,
                  role as SupportedMedicalTreatmentContactRole,
                ),
              ]
            : [],
      );
      const result = await createMedicalTreatment.mutateAsync({
        matterId: matter.id,
        ...treatmentData,
        type: data.type ?? [],
        connections,
      });
      resData = result;
      updateSlideoverPayload("medical-treatment", {
        title: SLIDEOVER_TITLE.MEDICAL_TREATMENT.UPDATE,
        description: SLIDEOVER_DESCRIPTION.MEDICAL_TREATMENT.UPDATE,
        editing: true,
        treatmentId: resData.data.id,
      });
    }

    if (!editing) {
      toast.success("Medical treatment created successfully");
    }

    if (editing && selectedTreatment) {
      const pendingUpdates = buildPendingBillUpdates(data, selectedTreatment);
      if (pendingUpdates) {
        setPendingBillUpdates(pendingUpdates.updates);
        setPendingBillUpdateSummary(pendingUpdates.summary);
        setIsUpdateBillsModalOpen(true);
        if (shouldCloseAfterSave) {
          setPendingCloseAfterSave(true);
          shouldCloseAfterSave = false;
        }
      }
    }

    if (shouldCloseAfterSave) {
      setOpen(false);
      const sanitizedResData = omitBy(resData.data, isNil);
      treatmentForm.reset({
        ...defaultFormValues,
        ...sanitizedResData,
        matterContactConnectionMedicalProvider:
          data.matterContactConnectionMedicalProvider,
        matterContactConnectionTreatingPhysician:
          data.matterContactConnectionTreatingPhysician,
      });
      setSelectedProvider(null);
      setSelectedTreatingPhysician(null);
    }
  };

  useEffect(() => {
    if (!open) {
      treatmentForm.reset();
    }
  }, [open, treatmentForm]);

  const handleUpdateBills = async () => {
    if (pendingBillUpdates.length === 0) {
      setIsUpdateBillsModalOpen(false);
      setPendingBillUpdateSummary(null);
      if (pendingCloseAfterSave) {
        setPendingCloseAfterSave(false);
        setOpen(false);
      }
      return;
    }
    try {
      await Promise.all(
        pendingBillUpdates.map((update) =>
          updateTreatmentBill.mutateAsync({
            matterId: matter.id,
            id: update.billId,
            dateModified: update.dateModified,
            ...update.updates,
          }),
        ),
      );
      await invalidateMatterAndSearchQueries(queryClient);
      toast.success(
        `Updated ${pendingBillUpdates.length} medical treatment bill${
          pendingBillUpdates.length === 1 ? "" : "s"
        }.`,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdateBillsModalOpen(false);
      setPendingBillUpdates([]);
      setPendingBillUpdateSummary(null);
      if (pendingCloseAfterSave) {
        setPendingCloseAfterSave(false);
        setOpen(false);
      }
    }
  };

  const lienStatusFormValue = treatmentForm.watch("lien");

  useEffect(() => {
    if (lienStatusFormValue) return;
    const lienHolderValue = treatmentForm.getValues(
      "matterContactConnectionLienHolder",
    );
    const lienRepresentativeValue = treatmentForm.getValues(
      "matterContactConnectionLienRepresentative",
    );
    if (lienHolderValue?.id) {
      treatmentForm.setValue(
        "matterContactConnectionLienHolder",
        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
        { shouldDirty: true },
      );
    }
    if (lienRepresentativeValue?.id) {
      treatmentForm.setValue(
        "matterContactConnectionLienRepresentative",
        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
        { shouldDirty: true },
      );
    }
    setSelectedLienHolder(null);
    setSelectedLienRepresentative(null);
  }, [lienStatusFormValue, treatmentForm]);

  const providerPreviewContact = getConnectionPreviewContact(provider);
  const providerFormContact = treatmentForm.watch(
    "matterContactConnectionMedicalProvider",
  );
  const activeProviderContact =
    selectedProvider?.id === providerFormContact?.id
      ? selectedProvider
      : providerPreviewContact?.id === providerFormContact?.id
        ? providerPreviewContact
        : undefined;
  const activeContactPhones = activeProviderContact?.phones ?? [];
  const activeContactEmails = activeProviderContact?.emails ?? [];
  const recordsPhones = activeContactPhones.filter(
    (p) =>
      p.label === "Medical Record Requests" ||
      p.label === "Billing and Medical Record Requests",
  );
  const recordsEmails = activeContactEmails.filter(
    (e) =>
      e.label === "Medical Record Requests" ||
      e.label === "Billing and Medical Record Requests",
  );
  const billingPhones = activeContactPhones.filter(
    (p) =>
      p.label === "Billing Record Requests" ||
      p.label === "Billing and Medical Record Requests",
  );
  const billingEmails = activeContactEmails.filter(
    (e) =>
      e.label === "Billing Record Requests" ||
      e.label === "Billing and Medical Record Requests",
  );
  const hasActiveProvider = Boolean(providerFormContact?.id);
  const hasRecordsContact =
    recordsPhones.length > 0 || recordsEmails.length > 0;
  const hasBillingContact =
    billingPhones.length > 0 || billingEmails.length > 0;
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatment,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatment,
    ACTION_PERMISSION_VALUES.delete,
  );
  const isFieldsetDisabled = editing && !canUpdate;
  const clearProviderSelection = () => {
    treatmentForm.setValue(
      "matterContactConnectionMedicalProvider",
      MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
      { shouldDirty: true },
    );
    setSelectedProvider(null);
  };

  const clearTreatingPhysicianSelection = () => {
    treatmentForm.setValue(
      "matterContactConnectionTreatingPhysician",
      MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
      { shouldDirty: true },
    );
  };

  return (
    <>
      <Form {...treatmentForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description ?? "Edit medical treatment details"}
          size="3xl"
          className="h-[95vh] max-w-5xl"
          bodyContained
          bodyClassName="min-h-0 px-0"
          loading={isFormLoading}
          isDirty={treatmentForm.formState.isDirty}
          preview={
            <div className="sticky top-0">
              <AddContactFormField
                label="Provider"
                register={treatmentForm.register(
                  "matterContactConnectionMedicalProvider",
                )}
                setSelectedContact={setSelectedProvider}
                defaultSearchObject={{
                  type: ["organization"],
                  organizationSubTypes: ["Medical Provider"],
                }}
                defaultSearchField="name"
                defaultValues={{
                  type: "organization",
                  organizationSubTypes: ["Medical Provider"],
                }}
                createNewContactLabel="Create new provider"
                disableSelectContactButton={isFieldsetDisabled}
                renderSelectedContactPreview={(contact) => (
                  <MedicalTreatmentContactPreview
                    contact={contact}
                    previewContact={selectedProvider ?? undefined}
                    connection={provider}
                    role="Provider"
                    matterId={matter.id}
                    onRemoveContact={
                      isFieldsetDisabled ? undefined : clearProviderSelection
                    }
                  />
                )}
                showEmbeddedCreateSuccessToast={false}
              />
              <div className="mt-4">
                <AddContactFormField
                  hideCompactContactChrome
                  contactSelectorSize="compact"
                  label="Treating physician"
                  register={treatmentForm.register(
                    "matterContactConnectionTreatingPhysician",
                  )}
                  setSelectedContact={setSelectedTreatingPhysician}
                  defaultSearchObject={{ type: ["individual"] }}
                  matterId={matter.id}
                  defaultSearchField="name"
                  defaultValues={{
                    type: "individual",
                    individualSubTypes: ["Medical Professional"],
                  }}
                  renderSelectedContactPreview={(contact) => (
                    <MedicalTreatmentContactPreview
                      contact={contact}
                      connection={treatingPhysician}
                      role="Treating Physician"
                      matterId={matter.id}
                      onRemoveContact={
                        isFieldsetDisabled
                          ? undefined
                          : clearTreatingPhysicianSelection
                      }
                    />
                  )}
                  showEmbeddedCreateSuccessToast={false}
                />
              </div>
            </div>
          }
          previewColumnClassName="lg:w-[min(100%,380px)] lg:max-w-[380px]"
          bodyFormId={MEDICAL_TREATMENT_FORM_ID}
          bodyFormOnSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await treatmentForm.handleSubmit(onSubmit)();
          }}
          footer={
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {editing && selectedTreatment && canDelete && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                    onClick={() => {
                      setIsDeleteTreatmentOpen(true);
                    }}
                  >
                    Delete treatment
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={treatmentForm.formState.isDirty}
                />
                <FormSubmitButton
                  variant="light-primary"
                  loading={isFormLoading}
                  disabled={editing && !canUpdate}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(false);
                  }}
                  submitFormId={MEDICAL_TREATMENT_FORM_ID}
                />
                <FormSubmitButton
                  variant="default"
                  loading={isFormLoading}
                  disabled={editing && !canUpdate}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  title="Save and close"
                  submitFormId={MEDICAL_TREATMENT_FORM_ID}
                />
              </div>
            </div>
          }
        >
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              {isFormLoading && <FormLoadingOverlay />}
              <fieldset disabled={isFieldsetDisabled} className="space-y-8 p-6">
                <section className="space-y-4">
                  <h3 className="text-foreground text-sm font-semibold">
                    Treatment info
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <MultiselectFormField
                      options={CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS.map(
                        (type) => ({ label: type, value: type }),
                      )}
                      label="Type*"
                      register={treatmentForm.register("type")}
                      placeholder="Select treatment types"
                      maxCount={1}
                    />
                    <SimpleSelectFormField
                      items={MEDICAL_TREATMENT_STATUS_OPTIONS}
                      label="Status"
                      placeholder="Select a status"
                      register={treatmentForm.register("status")}
                    />
                    <TextFormField
                      register={treatmentForm.register("patientAccountNumber")}
                      label="Patient account number"
                    />
                    <TextFormField
                      register={treatmentForm.register("portalLink")}
                      label="Portal link"
                      type="url"
                      placeholder="https://"
                    />
                    <SimpleSelectFormField
                      items={
                        MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS
                      }
                      label="Letter of protection"
                      placeholder="Select status"
                      clearable
                      register={treatmentForm.register("letterOfProtection")}
                    />
                    <SimpleSelectFormField
                      items={MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS}
                      label="Lien"
                      placeholder="Select status"
                      clearable
                      register={treatmentForm.register("lien")}
                    />
                    <div className="col-span-2">
                      <TextAreaFormField
                        register={treatmentForm.register("description")}
                        label="Description"
                      />
                    </div>
                    <div className="col-span-2">
                      <TextAreaFormField
                        register={treatmentForm.register("portalDetails")}
                        label="Portal details"
                      />
                    </div>
                  </div>
                </section>

                {hasActiveProvider && hasRecordsContact && (
                  <section className="space-y-4">
                    <h3 className="border-border text-foreground border-b pb-2 text-sm font-semibold">
                      Records contact
                    </h3>
                    <div className="space-y-2">
                      {recordsPhones.map((p, i) => (
                        <div
                          key={i}
                          className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          <span>{formatPhone(p)}</span>
                          {p.label && (
                            <span className="text-muted-foreground/60 text-xs">
                              ({p.label})
                            </span>
                          )}
                        </div>
                      ))}
                      {recordsEmails.map((e, i) => (
                        <div
                          key={i}
                          className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span>{e.address}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {hasActiveProvider && hasBillingContact && (
                  <section className="space-y-4">
                    <h3 className="border-border text-foreground border-b pb-2 text-sm font-semibold">
                      Billing contact
                    </h3>
                    <div className="space-y-2">
                      {billingPhones.map((p, i) => (
                        <div
                          key={i}
                          className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          <span>{formatPhone(p)}</span>
                          {p.label && (
                            <span className="text-muted-foreground/60 text-xs">
                              ({p.label})
                            </span>
                          )}
                        </div>
                      ))}
                      {billingEmails.map((e, i) => (
                        <div
                          key={i}
                          className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span>{e.address}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {lienStatusFormValue && (
                  <section className="space-y-4">
                    <h3 className="border-border text-foreground border-b pb-2 text-sm font-semibold">
                      Lien negotiations contact
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <AddContactFormField
                        label="Lien holder"
                        register={treatmentForm.register(
                          "matterContactConnectionLienHolder",
                        )}
                        setSelectedContact={setSelectedLienHolder}
                        defaultSearchObject={{}}
                        onDelete={() => {
                          treatmentForm.setValue(
                            "matterContactConnectionLienHolder",
                            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                            { shouldDirty: true },
                          );
                        }}
                        matterId={matter.id}
                        defaultSearchField="name"
                        showEmbeddedCreateSuccessToast={false}
                      />
                      <AddContactFormField
                        label="Lien representative"
                        register={treatmentForm.register(
                          "matterContactConnectionLienRepresentative",
                        )}
                        setSelectedContact={setSelectedLienRepresentative}
                        defaultSearchObject={{
                          individualSubTypes: ["Lien Representative"],
                        }}
                        onDelete={() => {
                          treatmentForm.setValue(
                            "matterContactConnectionLienRepresentative",
                            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                            { shouldDirty: true },
                          );
                        }}
                        matterId={matter.id}
                        defaultValues={{
                          type: "individual",
                          individualSubTypes: ["Lien Representative"],
                        }}
                        showEmbeddedCreateSuccessToast={false}
                      />
                    </div>
                  </section>
                )}

                <section className="space-y-4">
                  <h3 className="border-border text-foreground border-b pb-2 text-sm font-semibold">
                    Settings
                  </h3>
                  <div className="space-y-3">
                    <TooltipWrapper
                      message={
                        !!completeTreatment?.bills &&
                        completeTreatment.bills.length > 0
                          ? "Cannot enable if bills exist for this treatment."
                          : undefined
                      }
                    >
                      <SwitchFormField
                        register={treatmentForm.register("billNotApplicable")}
                        label={
                          <Typography variant="subHeading" size="sm">
                            Billing records not applicable
                          </Typography>
                        }
                        disabled={
                          !!completeTreatment?.bills &&
                          completeTreatment.bills.length > 0
                        }
                      />
                    </TooltipWrapper>
                    <TooltipWrapper
                      message={
                        !!completeTreatment?.records &&
                        completeTreatment.records.length > 0
                          ? "Cannot enable if records exist for this treatment."
                          : undefined
                      }
                    >
                      <SwitchFormField
                        register={treatmentForm.register("recordNotApplicable")}
                        label={
                          <Typography variant="subHeading" size="sm">
                            Medical records not applicable
                          </Typography>
                        }
                        disabled={
                          !!completeTreatment?.records &&
                          completeTreatment.records.length > 0
                        }
                      />
                    </TooltipWrapper>
                    <TooltipWrapper
                      message={
                        !!completeTreatment?.plans &&
                        completeTreatment.plans.length > 0
                          ? "Cannot enable if plans exist for this treatment."
                          : undefined
                      }
                    >
                      <SwitchFormField
                        register={treatmentForm.register("planNotApplicable")}
                        label={
                          <Typography variant="subHeading" size="sm">
                            Treatment plan not applicable
                          </Typography>
                        }
                        disabled={
                          !!completeTreatment?.plans &&
                          completeTreatment.plans.length > 0
                        }
                      />
                    </TooltipWrapper>
                  </div>
                </section>
              </fieldset>
            </div>
          </div>
        </AppModal>
      </Form>
      {editing && selectedTreatment && (
        <ActionConfirmationModal
          open={isDeleteTreatmentOpen}
          setOpen={setIsDeleteTreatmentOpen}
          title="Delete medical treatment?"
          description="This action cannot be undone. This will delete the medical treatment and all associated treatment dates, bills, records, and plans."
          buttonText="Delete treatment"
          isActionPending={deleteMedicalTreatment.isPending}
          afterActionFn={async () => {
            await deleteMedicalTreatment.mutateAsync({
              matterId: matter.id,
              id: selectedTreatment.id,
            });
            setOpen(false);
          }}
        />
      )}
      {pendingBillUpdateSummary && (
        <ActionConfirmationModal
          open={isUpdateBillsModalOpen}
          setOpen={(nextOpen) => {
            setIsUpdateBillsModalOpen(nextOpen);
            if (!nextOpen) {
              setPendingBillUpdates([]);
              setPendingBillUpdateSummary(null);
              if (pendingCloseAfterSave) {
                setPendingCloseAfterSave(false);
                setOpen(false);
              }
            }
          }}
          title="Update medical treatment bills?"
          description={`This medical treatment's LOP/Lien status changed. ${
            pendingBillUpdateSummary.lopCount > 0
              ? `${pendingBillUpdateSummary.lopCount} bill${
                  pendingBillUpdateSummary.lopCount === 1 ? "" : "s"
                } ${pendingBillUpdateSummary.lopCount === 1 ? "is" : "are"} missing LOP`
              : ""
          }${
            pendingBillUpdateSummary.lopCount > 0 &&
            pendingBillUpdateSummary.lienCount > 0
              ? " and "
              : ""
          }${
            pendingBillUpdateSummary.lienCount > 0
              ? `${pendingBillUpdateSummary.lienCount} bill${
                  pendingBillUpdateSummary.lienCount === 1 ? "" : "s"
                } ${
                  pendingBillUpdateSummary.lienCount === 1 ? "is" : "are"
                } missing Lien`
              : ""
          }. Update ${
            pendingBillUpdateSummary.total
          } bill${pendingBillUpdateSummary.total === 1 ? "" : "s"} to match this treatment?`}
          buttonText="Update bills"
          actionVariant="default"
          afterActionFn={handleUpdateBills}
        />
      )}
    </>
  );
};
